import type { ErrorCode } from "@/api/types";
import { ApiError } from "@/api/client";

/**
 * 백엔드 ErrorCode → 한국어 유저 메시지 매핑. 서버 message 가 와도 이 사전을 우선.
 * docs/api/error-codes.md 참조.
 */

const KR: Record<string, string> = {
    // 일반
    INVALID_PARAMETER: "요청에 잘못된 값이 있습니다.",
    RESOURCE_NOT_FOUND: "요청한 대상을 찾을 수 없습니다.",
    INTERNAL_ERROR: "서버에서 문제가 발생했습니다. 잠시 후 다시 시도해주세요.",

    // 신원
    IDENTITY_MISSING: "가문 식별자가 없습니다. 새 여정을 시작해주세요.",
    IDENTITY_MALFORMED: "가문 식별자가 손상되었습니다. 새 가문을 만들어주세요.",
    HOUSE_NOT_FOUND: "저장된 가문을 서버에서 찾지 못했습니다.",
    IDENTITY_MISMATCH: "이 런은 다른 가문에 속합니다.",
    HOUSE_NAME_CONFLICT: "이미 쓰이는 가문 이름입니다.",

    // 콘텐츠
    CONTENT_NOT_FOUND: "선택한 카드를 더 이상 사용할 수 없습니다.",

    // 런 상태
    BLOCKED_DESTINY: "이 이야기에서는 사용할 수 없는 운명이 포함됐습니다.",
    DESTINY_LIMIT_EXCEEDED: "운명은 최대 6장까지 선택할 수 있습니다.",
    DESTINY_LIMIT_INSUFFICIENT: "운명은 최소 1장 이상 필요합니다.",
    ACTION_NOT_ALLOWED: "이번 주에는 선택할 수 없는 행동입니다.",
    FIXED_EVENT_REQUIRED: "고정된 이야기 사건이 남아 있습니다.",
    RUN_ALREADY_COMPLETED: "이미 완료된 런입니다.",
    RUN_NOT_COMPLETED: "아직 완료되지 않은 런입니다.",
    ALREADY_FINALIZED: "이미 확정된 런입니다.",

    // Pending 이벤트
    PENDING_EVENTS_UNRESOLVED: "해결되지 않은 사건이 남아 있습니다.",
    EVENT_ALREADY_RESOLVED: "이미 처리된 사건입니다.",
    INVALID_CHOICE: "이 선택지는 이 사건에 속하지 않습니다.",

    // 멱등
    IDEMPOTENCY_KEY_REUSED: "같은 요청이 이미 처리 중입니다.",

    // 인벤토리·장비
    ITEM_NOT_FOUND: "해당 아이템 정보를 찾을 수 없습니다.",
    ITEM_NOT_OWNED: "소유하고 있지 않은 아이템입니다.",
    ITEM_NOT_EQUIPPABLE: "이 아이템은 장착할 수 없습니다.",
    ITEM_SLOT_MISMATCH: "이 슬롯에 넣을 수 없는 아이템입니다.",
    ITEM_REQUIREMENT_UNMET: "장착 조건을 충족하지 못했습니다.",
    ITEM_CLASS_RESTRICTED: "이 영혼 카드로는 장착할 수 없습니다.",
    ITEM_SCENARIO_RESTRICTED: "이 이야기에서는 장착할 수 없습니다.",
    ITEM_DESTINY_RESTRICTED: "특정 운명 체인이 필요합니다.",
    ITEM_ALREADY_EQUIPPED: "이미 같은 슬롯에 장착되어 있습니다.",
    ITEM_NOT_CONSUMABLE: "사용할 수 있는 아이템이 아닙니다.",

    // 상점
    ITEM_NOT_TRADABLE: "이 물건은 거래되지 않습니다.",
    ITEM_NOT_IN_STOCK: "재고가 부족합니다.",
    INSUFFICIENT_GOLD: "소지금이 부족합니다.",
};

export function translateErrorCode(code?: ErrorCode | string): string | undefined {
    if (!code) return undefined;
    return KR[code];
}

export function describeApiError(err: unknown): { title: string; message: string } {
    if (err instanceof ApiError) {
        const translated = translateErrorCode(err.code);
        return {
            title: translated ? "요청 실패" : `오류 ${err.status}`,
            message: translated ?? err.message ?? "알 수 없는 오류가 발생했습니다.",
        };
    }
    if (err instanceof Error) {
        return { title: "오류", message: err.message };
    }
    return { title: "오류", message: "알 수 없는 오류가 발생했습니다." };
}

/**
 * ITEM_REQUIREMENT_UNMET 같은 경우 details 에 missing map 이 있음.
 * 사용처: 장착 실패 시 구체적으로 어느 스탯이 부족한지 토스트로 보여주기.
 */
export function formatMissingRequirements(details?: Record<string, unknown>): string {
    if (!details) return "";
    const missing = details.missing as Record<string, number> | undefined;
    if (!missing || typeof missing !== "object") return "";
    const parts = Object.entries(missing).map(([key, val]) => `${key} ${val}`);
    return parts.length ? `부족: ${parts.join(", ")}` : "";
}
